'use client';
import { forwardRef, useCallback, useImperativeHandle, useRef } from 'react';
import { TRACE_EVENT_ID, TRACE_EVENT_NAME } from '@/app/constants';
import type { VideoPlayerRef } from '@/app/types';
import { cn, reportEvent } from '@/app/utils';
import { usePathname } from '@/i18n/routing';
import { BaseVideoPlayer } from './BaseVideoPlayer';
import type { VideoPlayerProps } from './types';

/*
 * 视频播放器组件，使用的地方：
 * 1.任务列表
 * 2.视频分享
 * 3.视频弹窗（生成物、短片、发现）
 * */
export const VideoPlayer = forwardRef<VideoPlayerRef, VideoPlayerProps>(
  (
    {
      className,
      videoClassName,
      src,
      poster,
      startTime,
      upscaleTag,
      autoPlay,
      playOnHover,
      hoverTogglesControls = false,
      hideControls = false,
      creationId,
      isLazy = false,
      hasAudio = false,
      defaultVolume = 0.6,
      exploreTab,
      handleClickVideo,
      onPlayEnd,
      containerStyle,
      initShowControls = false,
      onError,
      enableSpaceShortcut,
      showStartPlugin,
    },
    ref,
  ) => {
    const pathname = usePathname();
    const playerRef = useRef<VideoPlayerRef | null>(null);

    // 暴露 ref 方法给父组件
    useImperativeHandle(ref, () => ({
      player: playerRef.current?.player || null,
      play: async () => {
        if (playerRef.current) {
          await playerRef.current.play();
        }
      },
      pause: () => {
        playerRef.current?.pause();
      },
      destroy: () => {
        playerRef.current?.destroy();
      },
      getVideoElement: () => {
        return playerRef.current?.getVideoElement() || null;
      },
    }));

    // 处理视频点击事件
    const handleVideoClick = () => {
      if (handleClickVideo) {
        handleClickVideo();
        playerRef.current?.pause();
      }
      if (exploreTab) {
        reportEvent(TRACE_EVENT_NAME[exploreTab], {
          [TRACE_EVENT_ID[exploreTab]]: creationId ?? '',
          interaction_type: 'play',
          interaction_position: pathname,
          content_type: 'video',
        });
      }
    };

    const renderUpscaleTag = useCallback(() => {
      if (!upscaleTag) return null;
      return (
        <div className="absolute top-3 left-3 rounded-6 bg-black/50 px-2 py-1 text-system-white text-xs">
          {upscaleTag}
        </div>
      );
    }, [upscaleTag]);

    return (
      <div className={cn('relative h-full w-full', className)} onClick={handleVideoClick} style={containerStyle}>
        <BaseVideoPlayer
          ref={playerRef}
          className={videoClassName}
          src={src}
          poster={poster}
          startTime={startTime}
          autoPlay={autoPlay}
          isLazy={isLazy}
          hasAudio={hasAudio}
          defaultVolume={defaultVolume}
          initShowControls={initShowControls}
          hideControls={hideControls}
          playOnHover={playOnHover}
          onPlayEnd={onPlayEnd}
          handleClickVideo={handleClickVideo}
          hoverTogglesControls={hoverTogglesControls}
          onError={onError}
          enableSpaceShortcut={enableSpaceShortcut}
          showStartPlugin={showStartPlugin}
        />
        {renderUpscaleTag()}
      </div>
    );
  },
);

VideoPlayer.displayName = 'VideoPlayer';
